import { Button, Table, Tag, message } from "antd";
import React from "react";
import { RouteComponentProps } from "react-router";
import Card from "../components/Card";
import { ExportedData } from "../helpers/shared-interfaces";

import { CheckCircleOutlined, DownloadOutlined, UserOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import { ColumnType } from "antd/lib/table/interface";
import { AxiosResponse } from "axios";
import moment from "moment";
import {
  formatSnakeCase,
  getStateColor,
  injectKey,
  request,
  responseHandler,
  fetchEnumOptions,
} from "../helpers/utilities";
import {
  GET_EXPORTED_DATA,
  GET_EXPORT_DATA_STATUS_ENUM,
  GET_EXPORT_DATA_SORT_FIELDS,
} from "../gql/general";
import { PageToolbar } from "../components/PageToolbar";
import {
  hasActiveFilters,
  clearAllFilters,
  parseQueryParams,
  buildQueryString,
} from "../components/TableFilters";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  isLoading: boolean;
  exportedData: ExportedData[];
  exportedDataCount: number;
  page: number;
  pageSize: number;
  state: string[];
  stateOptions: Array<{ value: string; label: string; color?: string }>;
  sortField: string;
  sortDirection: string;
  sortFieldOptions: Array<{ value: string; label: string }>;
  fetchAll: boolean;
}

export function getColumns<
  T extends ExportedData = ExportedData,
>(): ColumnType<T>[] {
  return [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      render(_: string, record: ExportedData): JSX.Element {
        return <span>{record.id}</span>;
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render(_: string, record: ExportedData): JSX.Element {
        return <span>{formatSnakeCase(record?.name)}</span>;
      },
    },
    {
      title: "Exported By",
      dataIndex: ["user", "name"],
      key: "user",
      render(_: string, record: ExportedData): JSX.Element {
        return <span>{record.user?.name || "—"}</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "state",
      key: "state",
      render(text: string, record): JSX.Element {
        return (
          <Tag
            color={getStateColor(record.status)}
            style={{ textTransform: "capitalize" }}
          >
            {formatSnakeCase(record.status)}
          </Tag>
        );
      },
    },
    {
      title: "Failure Reason",
      dataIndex: "failureReason",
      key: "failureReason",
      render(_: string, record: ExportedData): JSX.Element {
        return <span>{record.failureReason || "—"}</span>;
      },
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render(_: string, record: ExportedData): JSX.Element {
        return (
          <span>
            {record.createdAt
              ? moment(record.createdAt, "YYYY-MM-DD HH:mm:ss").format("llll")
              : "N/A"}
          </span>
        );
      },
    },
    {
      title: "Download",
      dataIndex: "url",
      key: "url",
      render(url: string): JSX.Element {
        return url ? (
          <Button
            onClick={() => {
              window.open(url);
            }}
          >
            <DownloadOutlined />
          </Button>
        ) : (
          <></>
        );
      },
    },
  ];
}

export default class ExportDataRecords extends React.Component<
  RouteComponentProps,
  State
> {
  private _isMounted = false;

  constructor(props: Readonly<RouteComponentProps>) {
    super(props);
    this.state = {
      isLoading: true,
      exportedData: [],
      exportedDataCount: 0,
      page: 1,
      pageSize: 25,
      state: [],
      stateOptions: [],
      sortField: "CREATED_AT",
      sortDirection: "DESC",
      sortFieldOptions: [],
      fetchAll: false,
    };
    this.nextPage = this.nextPage.bind(this);
  }

  triggerImmediateSearch = async (): Promise<void> => {
    const { page, pageSize, state, sortField, sortDirection, fetchAll } =
      this.state;
    interface Response {
      count: number;
      results: ExportedData[];
    }

    this.setState({ isLoading: true });

    try {
      const filters: { statuses?: string[]; fetchAll?: boolean } = {};
      if (state.length > 0) {
        filters.statuses = state;
      }
      if (fetchAll) {
        filters.fetchAll = true;
      }

      const r: AxiosResponse = await request({
        query: GET_EXPORTED_DATA,
        variables: {
          page,
          numberPerPage: pageSize,
          filters: Object.keys(filters).length > 0 ? filters : undefined,
          orderBy: { field: sortField, direction: sortDirection },
        },
      });

      const { handled, mutation } = responseHandler<Response>(
        r,
        "getExportedData",
      );

      if (handled) {
        message.error("Failed to load exported data records");
      } else {
        this.syncBrowserQueryString();
        this.setState({
          exportedData: mutation.results,
          exportedDataCount: mutation.count,
        });
      }
    } catch (error) {
      message.error(`Search Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  };

  syncBrowserQueryString = (): void => {
    let queryStringResult = buildQueryString(
      {
        ...this.state,
        q: "",
        selectedStates: this.state.state,
      } as Parameters<typeof buildQueryString>[0],
      {
        includeSort: true,
      },
    );
    if (this.state.fetchAll) {
      queryStringResult += `${queryStringResult.includes("?") ? "&" : "?"}fetchAll=true`;
    }
    this.props.history.replace(queryStringResult);
  };

  async nextPage(page: number, pageSize: number): Promise<void> {
    this.setState({ page, pageSize }, async () => {
      await this.triggerImmediateSearch();
    });
  }

  mergeWithLocalState = (callback?: () => void): void => {
    const parsed = parseQueryParams({
      includeSort: true,
    });
    const urlParams = new URLSearchParams(window.location.search);

    this.setState(
      {
        page: parsed.page || 1,
        state: parsed.selectedStates || [],
        sortField: parsed.sortField || "CREATED_AT",
        sortDirection: parsed.sortDirection || "DESC",
        fetchAll: urlParams.get("fetchAll") === "true",
      },
      callback,
    );
  };

  fetchStateEnums = async (): Promise<void> => {
    const stateOptions = await fetchEnumOptions(GET_EXPORT_DATA_STATUS_ENUM, {
      includeColor: true,
      errorMsg: "Failed to fetch status options",
    });

    if (this._isMounted) {
      this.setState({ stateOptions });
    }
  };

  fetchSortFieldOptions = async (): Promise<void> => {
    const sortFieldOptions = await fetchEnumOptions(
      GET_EXPORT_DATA_SORT_FIELDS,
      {
        errorMsg: "Failed to fetch sort options",
      },
    );

    if (this._isMounted) {
      this.setState({ sortFieldOptions });
    }
  };

  handleStateFilter = (states: string[]) => {
    this.setState({ state: states, page: 1 }, () => {
      this.triggerImmediateSearch();
    });
  };

  handleSortChange = (field: string, direction: string): void => {
    this.setState(
      { sortField: field, sortDirection: direction, page: 1 },
      () => {
        this.triggerImmediateSearch();
      },
    );
  };

  async componentDidMount(): Promise<void> {
    this._isMounted = true;
    await Promise.all([this.fetchStateEnums(), this.fetchSortFieldOptions()]);

    this.mergeWithLocalState(() => {
      this.triggerImmediateSearch();
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render(): JSX.Element | null {
    const {
      exportedData,
      exportedDataCount,
      page,
      pageSize,
      sortField,
      sortDirection,
      fetchAll,
    } = this.state;

    const isFiltering = hasActiveFilters(
      {
        q: "",
        page,
        selectedStates: this.state.state,
        sortField,
        sortDirection,
      },
      {
        includeSort: true,
      },
    );

    return (
      <React.Fragment>
          <PageToolbar modelName="records"
            totalCount={exportedDataCount}
            currentDataCount={exportedData.length}
            page={page}
            pageSize={pageSize}
            filters={
              <PageToolbar.Filters
                isFiltering={isFiltering || fetchAll}
                onClear={() => {
                  this.setState({ fetchAll: false }, () => {
                    clearAllFilters(this);
                  });
                }}
                configs={[
                  {
                    label: "Exported By",
                    options: [
                      { value: "me", label: "My Exports" },
                      { value: "all", label: "All Users" },
                    ],
                    selectedValues: fetchAll ? ["all"] : ["me"],
                    onChange: (values: string[]) => {
                      const newFetchAll = values.includes("all");
                      this.setState(
                        { fetchAll: newFetchAll, page: 1 },
                        () => {
                          this.triggerImmediateSearch();
                        },
                      );
                    },
                    width: 180,
                    icon: <UserOutlined />,
                  },
                  {
                    label: "Status",
                    options: this.state.stateOptions,
                    selectedValues: this.state.state,
                    onChange: this.handleStateFilter,
                    width: 180,
                    icon: <CheckCircleOutlined />,
                  },
                ]}
                sort={{
                  field: sortField,
                  direction: sortDirection,
                  options: this.state.sortFieldOptions,
                  onChange: this.handleSortChange,
                }}
              />
            }
          />
          <Card>
            <div className="pt-table-container">
              <Table
                loading={this.state.isLoading}
                scroll={{ x: 500 }}
                columns={getColumns()}
                dataSource={injectKey<ExportedData>(exportedData, "id")}
                pagination={{
                  current: page,
                  pageSize: pageSize,
                  total: exportedDataCount,
                  onChange: (p, s) => this.nextPage(p, s),
                }}
              />
            </div>
          </Card>
      </React.Fragment>
    );
  }
}
